import { toTitleCase } from '@vrplatform/utils';
import type { HasuraClient } from '@vrplatform/graphql';
import {
  type StatementSummaryUnion,
  getFormattedStatement,
  getStatementQuery,
} from '../statements';

type CsvRow = Record<string, number | string>;

export const ownerStatementCsvQuery = async (
  client: HasuraClient,
  args: {
    groupedBy: StatementSummaryUnion;
    ownerId: string | undefined;
    statementIds: string[];
    teamId: string;
  }
) => {
  const statements = await client.query((q) =>
    getStatementQuery(
      q,
      {
        id: { _in: args.statementIds },
      },
      {
        teamId: args.teamId,
      }
    )
  );

  const { expenses, header, groupedBy, netRevenue } = getFormattedStatement({
    groupedBy: args.groupedBy,
    ownerId: args.ownerId,
    statements,
    type: 'owner',
  });

  const currency = header?.statement.currency || 'usd';

  const emptyExpenseRow: CsvRow = {
    [groupedBy === 'groupByReservation'
      ? 'Date'
      : toTitleCase(groupedBy) || 'missing']: '',
    description: '',
    type: '',
    amount: '',
  };

  const netRevRows = netRevenue.rows.map((row) => {
    const res: CsvRow = { ...emptyExpenseRow };

    if (groupedBy !== 'groupByReservation') {
      res[toTitleCase(groupedBy) || ''] = row.name || '';
    }

    Object.entries(row?.columnValues || {}).forEach(([key, value]) => {
      const column = netRevenue?.tableColumns.find((i) => i.id === key);

      if (column) {
        res[column.name] = value.value;
      }
    });

    res.currency = currency;

    return res;
  });

  const emptyNetRevRow: CsvRow = {};
  Object.keys(netRevRows[0] || {}).forEach((key) => {
    emptyNetRevRow[key] = '';
  });

  const expensesRows = expenses.sections.flatMap((section) => {
    const nameKey =
      groupedBy === 'groupByReservation'
        ? 'Date'
        : toTitleCase(groupedBy) || 'missing';

    if (groupedBy === 'groupByReservation') {
      if (section.aggregated)
        return [
          {
            ...emptyNetRevRow,
            [nameKey]: section.title,
            description: '',
            type: '',
            amount: section.total,
            currency,
          },
        ];
      else {
        return section.rows.map((row) => {
          return {
            ...emptyNetRevRow,
            [nameKey]: row.name,
            description: row.description,
            type: section.showTypeColumn ? row.type : '',
            amount: row.value,
            currency,
          };
        });
      }
    } else {
      return section.rows.map((row) => {
        return {
          ...emptyNetRevRow,
          [nameKey]: section.title,
          description: row.name,
          type: '',
          amount: row.value,
          currency,
        };
      });
    }
  });

  const rows = [...netRevRows, ...expensesRows];

  return {
    rows,
    listingName: header?.listing?.name || '',
  };
};
